import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The *Follow-up Network* shell — the directory list and its map.
 *
 * TOP-LEVEL, NOT A THIRD TAB UNDER `/follow-ups`. Legacy registers Network as
 * its own group extension (`themes/wbs/modules/follow-up/
 * class-follow-up-network.php:9-18`), a SIBLING of the follow-up tab set rather
 * than a sub-tab of it, and D24 re-homes group tabs to top-level routes — so
 * the faithful re-home of a sibling tab is a sibling route. It inherits nothing
 * from the `/follow-ups` shell and carries its own nav entries.
 *
 * The map is a `map` ACTION VARIABLE on the same slug in legacy, resolved by
 * `WBS_Group_Extension::templateFile()`'s fallback chain rather than by a
 * registered route — so `/MAP` and `/bogus` both render the LIST with a 200
 * there. Here they 404 (DIV-15): TanStack routes are declared, and a catch-all
 * that swallowed typos would be a second routing mechanism for a URL nobody
 * types — the toggle is a button.
 *
 * THIS SHELL CARRIES A REAL GATE, unlike `follow-ups.tsx`, and the difference
 * is structural rather than a change of mind: that route is the parent of a
 * detail whose server gate admits the student, the student's study helper and
 * an admin, so a profile-type test there would bounce three legitimate viewers.
 * Both children here ARE the directory, so the client gate can mirror the
 * server `Requirement` exactly.
 *
 * Defence in depth over the same set the two endpoints enforce, never the only
 * gate.
 */
function FollowUpNetworkLayout(): ReactElement | null {
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper` — that folds
  // FOLLOWUP_COORDINATOR, FOLLOWUP_PARTNER, STUDY_HELPER and ADMIN together
  // (`hooks/useCurrentUser.ts:14-19`) and would admit the study helpers the
  // endpoint refuses.
  const allowed =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
    me?.profile_type === 'FOLLOWUP_PARTNER' ||
    // `admin_bypass` is ON in the server's `FOLLOW_UP_NETWORK_REQUIREMENT`
    // (legacy's `parentCheck = true` lets any `bp_moderate` holder visit the
    // tab), so an admin gets a 200 from both endpoints; a client gate that
    // omitted them would render NotFoundScreen over a working response. There
    // is deliberately no admin NAV item — see `navigationConfig.ts`.
    me?.profile_type === 'ADMIN';

  // `/users/me` is pending on first paint and a bare `!allowed` bounces every
  // caller, the coordinator included.
  if (isPending) return null;
  if (!allowed) return <NotFoundScreen />;

  // No title and no toggle here: legacy emits a `pagetitle-header` block PER
  // TEMPLATE, each with its own `h3` and a button pointing at the other view,
  // and the two headings differ (`Follow-up Network` vs `Follow-up Network
  // Map`). Hoisting the header would lose the map's.
  return <Outlet />;
}

export const Route = createFileRoute('/_app/follow-up-network')({
  component: FollowUpNetworkLayout,
});
